import { photoTagStatus } from '@backend/src/lib/lead/constants';
import {
  LeadPhoto,
  LeadPhotoTag,
  LeadRoom,
} from '@backend/src/frontend-mongo-interfaces/mongoose.gen';
// import { HIGH_CONFIDENCE } from '@backend/src/lib/photo';
import { itemsBySpace } from 'lib/space';
import { InventoryApiResponse } from 'hooks/useInventoryDefinitions';

export const isPhotoTagConfirmed = (
  tag: Pick<LeadPhotoTag, 'quantity'>,
): boolean => (tag.quantity || 0) > 0;

export const isPhotoTagTmpNeeedsReview = (
  tag: Pick<LeadPhotoTag, 'tmpStatus'>,
): boolean => tag.tmpStatus === photoTagStatus.NeedsReview;

export const isPhotoTagNeeedsReview = (
  tag: Pick<LeadPhotoTag, 'status'>,
): boolean => tag.status === photoTagStatus.NeedsReview;

export const isPhotoTagRejected = (
  tag: Pick<LeadPhotoTag, 'status'>,
): boolean => tag.status === photoTagStatus.Rejected;

export const isPhotoTagReviewed = (
  tag: Pick<LeadPhotoTag, 'status'>,
): boolean => tag.status === photoTagStatus.Reviewed;

// export const isPhotoTagHighConfidence = (
//   tag: Pick<LeadPhotoTag, 'Confidence'>,
// ): boolean => (tag.Confidence || 0) >= HIGH_CONFIDENCE;

export const formatPhotoTagTitle = ({
  inventoryDefinitions,
  tag,
}: {
  inventoryDefinitions?: InventoryApiResponse;
  tag: LeadPhotoTag;
}): string | undefined => {
  return tag.itemType && inventoryDefinitions
    ? inventoryDefinitions.inventoryKeyedById[tag.itemType].label
    : tag.Name;
};

export const findLeadPhotoTagById = ({
  tags,
  tagId,
}: {
  tags: LeadPhotoTag[];
  tagId: LeadPhotoTag['_id'];
}): LeadPhotoTag | undefined => tags.find((tag) => tag._id === tagId);

export type LeadPhotoTagInfo = {
  photo: LeadPhoto;
  tag: LeadPhotoTag;
  tags: LeadPhotoTag[];
  index: number;
  total: number;
  hasNext: boolean;
  hasPrevious: boolean;
  nextTag: LeadPhotoTag;
  previousTag: LeadPhotoTag;
};

export const getLeadPhotoTagInfo = ({
  photo,
  tagId,
  unconfirmedOnly = false,
}: {
  photo: LeadPhoto;
  tagId: LeadPhotoTag['_id'];
  unconfirmedOnly?: boolean;
}): LeadPhotoTagInfo => {
  const tags = unconfirmedOnly
    ? photo.tags.filter(isPhotoTagTmpNeeedsReview)
    : photo.tags;

  const index = tags.findIndex((tag) => tag._id === tagId);
  const tag = photo.tags.find((tag) => tag._id === tagId) as LeadPhotoTag;
  const total = tags.length;
  const hasNext = index < total - 1;
  const nextTag = hasNext ? tags[index + 1] : tag;
  const hasPrevious = index !== 0;
  const previousTag = hasPrevious ? tags[index - 1] : tag;

  return {
    photo,
    tag,
    tags,
    index,
    total,
    hasNext,
    hasPrevious,
    nextTag,
    previousTag,
  };
};

export const leadPhotoTagsBySpaceAndType = ({
  spaceId,
  itemType,
  leadPhotos,
}: {
  spaceId: LeadRoom['_id'];
  itemType: number;
  leadPhotos: LeadPhoto[];
}): (LeadPhotoTag & { photo: LeadPhoto })[] => {
  const photos = itemsBySpace(spaceId, leadPhotos);

  const tags = photos
    .map((photo) => {
      if (typeof photo.tags === 'undefined') {
        return [];
      }

      return (photo.tags || []).map((tag) => ({
        ...tag,
        photo,
      }));
    })
    .flat()
    .filter((tag) => tag && `${tag.itemType}` === `${itemType}`);

  return tags;
};

export const unconfirmedLeadPhotoTagsBySpaceAndType = ({
  spaceId,
  leadPhotos,
}: {
  spaceId: LeadRoom['_id'];
  leadPhotos: LeadPhoto[];
}): (LeadPhotoTag & { photo: LeadPhoto })[] => {
  const photos = itemsBySpace(spaceId, leadPhotos);

  const tags = photos
    .map((photo) => {
      if (typeof photo.tags === 'undefined') {
        return [];
      }

      return (photo.tags || []).map((tag) => ({
        ...tag,
        photo,
      }));
    })
    .flat()
    .filter((tag) => tag && !isPhotoTagConfirmed(tag));

  return tags;
};
